<template>
  <div id="ideal_flights" class="form-group">
    <h3 v-if="!tripCreated">Step {{ currentStep }}. Your flight</h3>
    <h3 v-else>Your flight</h3>
    <template v-if="showRadios">
      <div class="infomsg">We recommend selecting your flights now to avoid price increases. However, if you require different flights, you may select to work with a travel expert.</div>
      <div v-if="flightError" class="error">{{ flightError }}</div>
      <div class="form-row">
        <div class="radiolabel" :class="{ on: !formData.prefersExpert }" @click="formData.prefersExpert = false">
          <div class="radio" />
          <label>Select your flights below (and lock in the price)<span class="pill">Recommended</span></label>
        </div>
      </div>
      <div class="form-row">
        <div class="radiolabel" :class="{ on: formData.prefersExpert }" @click="formData.prefersExpert = true">
          <div class="radio" />
          <label>Work with a travel expert to build a different itinerary</label>
        </div>
      </div>
    </template>

    <Result v-if="tripCreated" v-model:error="bookingErrorLocal" />

    <template v-if="showTextarea">
      <div class="form-row additional">
        <textarea :value="formData.additionalRequestDetails" placeholder="Provide any specific flight preferences (optional)" @input="updateAdditionalRequests($event.target.value)" />
      </div>
      <template v-if="props.config?.features?.is_flight_type_shown">
        <div class="form-row">How would you like to travel?</div>
        <LanderFlightType v-model="formData.typeIsGroup" :config="props.config" />
      </template>
      <Submit v-if="tripCreated && formData.prefersExpert" class="block" :is-loading="bookingLoading" text="Submit Your Request" @click="emit('request-expert')" />
    </template>

    <div v-if="canShowFlightSearch && !hasNoResults" v-show="showFlightSection" id="flights">

      <div v-if="!formData.flightsSelected">
        <FlightFilter v-model:filter="flightFilter" v-model:sort="flightSort" :is-lander="true" :show-chaperoned="props.config?.features?.is_flight_type_shown !== false">
          <template #instruction>
            <div ref="instructionRef" class="inline" :class="{ lookatme: isInstructionHighlighted }">
              <template v-if="formData.legIndex === 0">Task 1. Select your outbound flight to your program</template>
              <template v-else-if="formData.legIndex === 1"><a href="#" class="breadcrumb-link" @click.prevent="goToStep(0)">Task 1</a><span class="icon">&gt;</span>Task 2. Select your flight back home</template>
            </div>
          </template>
        </FlightFilter>
        <FlightList v-model:filter="flightFilter" v-model:sort="flightSort" :flights="flightParams" :leg-index="formData.legIndex" :organization-id="programFull.organization_id" :is-lander="true" :wants-chaperoned="formData.typeIsGroup === 'Chaperoned'" :is-dev="false" :program="programFull" :inbound-to="formData.homeAirportRtIsDiff ? formData.homeAirportTo : formData.homeAirportFrom" :cache-bust="cacheBust" @update:leg-index="updateLegIndex" @update:is-saved-flights="handleFlightsSaved" @reset-filter-sort="resetFilterSort" @search-results-available="handleSearchResultsAvailable" @no-flights-found="handleNoFlightsFound" />
      </div>

      <div v-if="formData.flightsSelected" id="new">
        <div ref="instructionRef" class="ssmsg" :class="{ lookatme: isInstructionHighlighted }">
          <a href="#" class="breadcrumb-link" @click.prevent="goToStep(0)">Task 1</a><span class="icon">&gt;</span><a href="#" class="breadcrumb-link" @click.prevent="goToStep(1)">Task 2</a><span class="icon">&gt;</span>Task 3. Select your fare
        </div>
        <FlightSummary :leg-index="formData.legIndex" :flights="flightParams" :program="programFull" :is-lander="true" @update:leg-index="updateLegIndex" @update:is-saved-flights="handleFlightsSaved" />
        <Submit v-if="tripCreated" class="block" :is-loading="bookingLoading" text="Book This Flight" @click="emit('book-flight')" />
      </div>
    </div>

    <div v-if="isDev && formData.flightsSelected" class="dev-simulate">
      <div>DEV ONLY - Simulate Booking Result</div>
      <label><input v-model="simulateLocal" type="radio" name="simulate" value="" /> Real booking</label>
      <label><input v-model="simulateLocal" type="radio" name="simulate" value="success" /> Success</label>
      <label><input v-model="simulateLocal" type="radio" name="simulate" value="success_pending" /> Success: pending</label>
      <label><input v-model="simulateLocal" type="radio" name="simulate" value="success_approval" /> Success: approval</label>
      <label><input v-model="simulateLocal" type="radio" name="simulate" value="failure_retry_card_declined" /> Retry: card declined</label>
      <label><input v-model="simulateLocal" type="radio" name="simulate" value="failure_retry_offer_expired" /> Retry: offer expired</label>
      <label><input v-model="simulateLocal" type="radio" name="simulate" value="failure_retry_price_increased" /> Retry: price increased</label>
      <label><input v-model="simulateLocal" type="radio" name="simulate" value="failure_fatal_escalate" /> Fatal: escalate</label>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, watch, computed, nextTick } from "vue";
import Result from "./common/FormResult.vue";
import Submit from "./common/FormSubmit.vue";
import LanderFlightType from "./LanderFlightType.vue";
import FlightFilter from "./TripNewBookFlightsOptionsFlightsFilterSort.vue";
import FlightList from "./TripNewBookFlightsOptionsList.vue";
import FlightSummary from "./TripNewBookFlightsSummaryBook.vue";

const props = defineProps({
  config: {
    type: Object,
    required: false,
    default: null,
  },
  currentStep: {
    type: Number,
    required: true,
  },
  skipSelfSearch: {
    type: Boolean,
    required: true,
  },
  searchParamsReady: {
    type: Boolean,
    required: true,
  },
  programFull: {
    type: Object,
    required: true,
  },
  flightParams: {
    type: Object,
    required: true,
  },
  flightError: {
    type: String,
    default: "",
  },
  cacheBust: {
    type: Number,
    default: 0,
  },
  tripCreated: {
    type: Boolean,
    default: false,
  },
  bookingError: {
    type: [String, Error, null],
    default: null,
  },
  bookingLoading: {
    type: Boolean,
    default: false,
  },
  simulate: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["book-flight", "request-expert", "update:simulate", "update:bookingError"]);

const isDev = import.meta.env.DEV;
const { formData } = inject("landerState");

// Local proxy for simulate so the parent's ref gets updated via the two-way binding
const simulateLocal = computed({
  get: () => props.simulate,
  set: val => emit("update:simulate", val),
});

// Local proxy for bookingError for the Result v-model
const bookingErrorLocal = computed({
  get: () => props.bookingError,
  set: val => emit("update:bookingError", val),
});

const flightFilter = ref({
  depart: "",
  name: "",
  policy: false,
  chaperoned: "Undecided",
  stops: "",
});
const flightSort = ref("recommended");
const instructionRef = ref(null);
const isInstructionHighlighted = ref(false);

const goToStep = step => {
  formData.flightsSelected = false;
  formData.searchResultsReady = false;
  formData.flightResultsCount = 0;
  formData.legIndex = step;
};

const canShowFlightSearch = computed(() => props.tripCreated || (props.config?.options?.is_auto_book && !props.skipSelfSearch && props.searchParamsReady));
const hasNoResults = computed(() => !props.tripCreated && formData.searchResultsReady && formData.flightResultsCount === 0);
const showRadios = computed(() => {
  // Always show radios when tripCreated so customer can switch to expert after errors
  if (props.tripCreated) return true;
  return canShowFlightSearch.value && !formData.flightsSelected && !hasNoResults.value;
});
const showFlightSection = computed(() => formData.flightsSelected || (canShowFlightSearch.value && !formData.prefersExpert && !hasNoResults.value));
const showTextarea = computed(() => !formData.flightsSelected && (!canShowFlightSearch.value || formData.prefersExpert || hasNoResults.value));

watch(
  () => props.config?.options?.is_auto_book && props.searchParamsReady && !props.skipSelfSearch,
  isActive => {
    if (isActive && formData.legIndex < 0) {
      formData.legIndex = 0;
    }
  },
  { immediate: true }
);

const updateAdditionalRequests = value => {
  formData.additionalRequestDetails = value;
};

const updateLegIndex = value => {
  formData.legIndex = value;
  formData.searchResultsReady = false;
  formData.flightResultsCount = 0;
};

const handleSearchResultsAvailable = count => {
  formData.flightResultsCount = count;
  if (count > 0) {
    formData.searchResultsReady = true;
  }
};

const handleFlightsSaved = value => {
  formData.flightsSelected = value;
  if (!value) {
    formData.searchResultsReady = false;
    formData.flightResultsCount = 0;
  }
};

const resetFilterSort = () => {
  flightFilter.value = {
    depart: "",
    name: "",
    policy: false,
    chaperoned: "Undecided",
    stops: "",
  };
  flightSort.value = "recommended";
};

const handleNoFlightsFound = () => {
  formData.searchResultsReady = true;
};

watch(
  () => [formData.legIndex, formData.flightsSelected],
  ([newLegIndex, newFlightsSelected], [oldLegIndex]) => {
    if ((newLegIndex === 1 && oldLegIndex === 0) || newFlightsSelected) {
      nextTick(() => {
        instructionRef.value?.scrollIntoView({ behavior: "smooth", block: "start" });
        isInstructionHighlighted.value = true;
        setTimeout(() => {
          isInstructionHighlighted.value = false;
        }, 500);
      });
    }
  }
);
</script>
